import { Component } from '@theme/component';

/**
 * "Build Your Domino Set" — a four-step configurator that ends in Shopify's own
 * cart.
 *
 * Design notes worth knowing before changing anything:
 *
 * - Shopify owns every price. Variant prices come from /products/{handle}.js and
 *   the case comes from its own variant, so discounts, taxes and checkout behave
 *   exactly as they do for a normal product. Nothing here calculates a total the
 *   cart will not honour; the review figure is a sum of real line prices.
 * - The set, the case and any fee go in as one /cart/add.js call with several
 *   items, so a set can never land half-added.
 * - Product data is fetched per collection on selection rather than rendered for
 *   all nine up front, because this is used on cellular at events.
 *
 * @extends Component
 */
class DominoBuilder extends Component {
  /** @type {string[]} */
  #steps = ['collection', 'set', 'designs', 'review'];
  #stepIndex = 0;

  /** Per-product config rendered by Liquid, keyed by handle. */
  #config = new Map();
  /** Product payloads from /products/{handle}.js, keyed by handle. */
  #products = new Map();
  /** Handles already being fetched, so a double tap does not double request. */
  #inflight = new Map();

  #state = {
    handle: null,
    package: 'dominoes',
    caseVariantId: null,
    mode: 'single',
    design: null,
    color: null,
    /** @type {Record<string, number>} keyed by "design||color" */
    counts: {},
  };

  #storageKey = 'sogenius:domino-builder';
  /**
   * design name -> 'per-color' | 'single-file' | 'none'.
   *
   * The filenames are the source of truth. A design with -red/-black files is
   * single-color and follows the shopper's choice. A design with only one file
   * is multi-color, so there is nothing to choose. A design with no artwork at
   * all is not multi-color — it is unfinished, and must keep behaving normally
   * rather than silently hiding the choice and charging the top tier.
   */
  #artState = new Map();

  /** Pending auto-dismiss for the error toast. */
  #errorTimer = 0;

  /** Whether the sticky tally's list of picks is open. Off by default so it
   *  stays a one-line bar until the shopper asks for the detail. */
  #tallyOpen = false;

  connectedCallback() {
    super.connectedCallback();

    for (const node of this.querySelectorAll('[data-domino-config]')) {
      try {
        const parsed = JSON.parse(node.textContent ?? '{}');
        if (parsed.handle) this.#config.set(parsed.handle, parsed);
      } catch (error) {
        console.error('[domino-builder] could not read config for', node.dataset.dominoConfig, error);
      }
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-db-next]')?.addEventListener('click', this.#onNext);
    this.querySelector('[data-db-back]')?.addEventListener('click', this.#onBack);
    this.querySelector('[data-db-add]')?.addEventListener('click', this.#onAdd);

    // Warm the product data as soon as a card is touched, before the tap lands.
    for (const input of this.querySelectorAll('[data-db-collection]')) {
      const label = input.closest('label');
      label?.addEventListener('pointerenter', () => this.#loadProduct(input.value), { once: true });
      label?.addEventListener('touchstart', () => this.#loadProduct(input.value), { once: true, passive: true });
    }

    this.#applyEvent();
    this.#restore();
    this.#render();
    this.#openRequestedCollection();
    this.#track('domino_builder_started', {});
  }

  /**
   * ?collection=<handle> arrives from a product page's "Build your set" link.
   * It lands on step one with that collection already checked, rather than
   * skipping ahead: seeing the choice made, next to the alternatives, is what
   * tells someone they are in the right place. The product is loaded up front
   * anyway, so Continue works on the first press. Applied after #restore, so a
   * link always wins over whatever the last visit left behind.
   */
  async #openRequestedCollection() {
    const handle = new URLSearchParams(location.search).get('collection');
    if (!handle || !this.#config.has(handle)) return;

    this.#state.handle = handle;
    this.#state.design = null;
    this.#state.color = null;
    this.#state.counts = {};
    this.#state.caseVariantId = null;

    const input = this.querySelector(`[data-db-collection][value="${CSS.escape(handle)}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;

    this.#stepIndex = 0;
    this.#render();

    await this.#loadProduct(handle);
    this.#render();
    this.#track('collection_selected', { handle, source: 'product-link' });
  }

  // ---------------------------------------------------------------- event mode

  /**
   * Reads ?event= and writes it to cart attributes. Cart-wide, set once, never
   * typed by anyone at a booth. Shopify Flow can read it off the order.
   */
  async #applyEvent() {
    const param = this.dataset.eventParam || 'event';
    const raw = new URLSearchParams(location.search).get(param);
    if (!raw) return;

    // A slug cannot tell an acronym from a word, so guessing produces things
    // like "Fwf Bashment". Look the code up in the merchant's own list first
    // and only fall back to title case.
    const label = this.#eventLabels().get(raw.toLowerCase()) ?? raw
      .split('-')
      .map((word) => (/^\d+$/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join(' ');

    const badge = this.querySelector('[data-db-event]');
    if (badge instanceof HTMLElement) {
      badge.textContent = `Ordering from ${label}`;
      badge.hidden = false;
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Event Source': label } }),
      });
    } catch (error) {
      // The order still gets the properties; only the attribution is lost.
      console.error('[domino-builder] could not record the event source', error);
    }
  }

  /** @returns {Map<string,string>} slug → display name, from section settings */
  #eventLabels() {
    const map = new Map();
    for (const line of (this.dataset.eventLabels || '').split('\n')) {
      const [slug, ...rest] = line.split('=');
      const name = rest.join('=').trim();
      if (slug?.trim() && name) map.set(slug.trim().toLowerCase(), name);
    }
    return map;
  }

  // ------------------------------------------------------------------ data

  /** @param {string} handle */
  async #loadProduct(handle) {
    if (!handle || this.#products.has(handle)) return this.#products.get(handle);
    if (this.#inflight.has(handle)) return this.#inflight.get(handle);

    const request = fetch(`/products/${handle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status} for ${handle}`);
        return response.json();
      })
      .then((product) => {
        this.#products.set(handle, product);
        this.#inflight.delete(handle);
        return product;
      })
      .catch((error) => {
        this.#inflight.delete(handle);
        console.error('[domino-builder] could not load', handle, error);
        return null;
      });

    this.#inflight.set(handle, request);
    return request;
  }

  #product() {
    return this.#state.handle ? this.#products.get(this.#state.handle) : null;
  }

  #conf() {
    return this.#state.handle ? this.#config.get(this.#state.handle) : null;
  }

  #setSize() {
    return this.#conf()?.setSize || 28;
  }

  /** Option values for a named option, with retired designs stripped. */
  #optionValues(name) {
    const product = this.#product();
    if (!product) return [];
    const option = product.options?.find((o) => o.name === name);
    if (!option) return [];
    const excluded = this.dataset.excluded || '|';
    return option.values.filter((v) => !excluded.includes(`|${String(v).trim().toLowerCase()}|`));
  }

  /**
   * Probes the first color's file to decide whether a design is single-color.
   * Re-renders once when the answer arrives, since it changes both the color
   * step and the price.
   * @param {string} design
   */
  #classify(design) {
    if (!design || this.#artState.has(design)) return;
    const conf = this.#conf();
    const art = conf?.art?.[design];
    const colors = this.#optionValues(this.#colorName());
    const firstUrl = colors.length ? art?.[colors[0]] : null;

    if (!art?.['']) {
      this.#artState.set(design, 'none');
      return;
    }

    const settle = (value) => {
      this.#artState.set(design, value);
      if (this.#state.design === design) return this.#render();
      // In a mixed set the answer decides whether this design gets one cell or
      // one per color, so redraw the grid -- but only the grid, or every
      // design settling would trigger a full re-render apiece.
      if (this.#state.mode === 'mixed') {
        this.#renderMatrix(this.#optionValues(this.#designName()), this.#optionValues(this.#colorName()));
      }
    };
    const probeBase = () => {
      const probe = new Image();
      probe.addEventListener('load', () => settle('single-file'));
      probe.addEventListener('error', () => settle('none'));
      probe.src = art[''];
    };

    if (!firstUrl) return probeBase();

    const probe = new Image();
    probe.addEventListener('load', () => settle('per-color'));
    probe.addEventListener('error', probeBase);
    probe.src = firstUrl;
  }

  /**
   * True only when a design has exactly one artwork file. A design with no
   * artwork is not multi-color, so it keeps its color choice and its price.
   */
  #isMultiColor() {
    const design = this.#state.design;
    return Boolean(design) && this.#artState.get(design) === 'single-file';
  }

  /**
   * The color a product should land on before the shopper touches anything.
   * Matched case-insensitively so "black" and "Black" both work, and skipped
   * silently when a product does not offer it.
   * @param {string[]} colors
   */
  #defaultColor(colors) {
    const wanted = (this.dataset.defaultColor || '').trim().toLowerCase();
    if (!wanted) return null;
    return colors.find((c) => String(c).trim().toLowerCase() === wanted) ?? null;
  }

  // Per product first: the catalog does not name these options consistently,
  // and Liquid has already worked out which ones this product actually uses.
  #designName() { return this.#conf()?.designOption || this.dataset.designOption || 'Design'; }
  #colorName() { return this.#conf()?.colorOption || this.dataset.colorOption || 'Domino Design Color'; }

  // ------------------------------------------------------------------ pricing

  /**
   * The cheapest real variant matching a design and color. Everything shown to
   * the shopper comes from here, so the review total is always a sum of prices
   * Shopify will actually charge.
   */
  #variantFor(design, color) {
    const product = this.#product();
    if (!product) return null;
    const names = product.options?.map((o) => o.name) ?? [];
    const wanted = { [this.#designName()]: design, [this.#colorName()]: color };

    const matches = product.variants.filter((variant) =>
      names.every((name, index) => {
        const want = wanted[name];
        if (want == null) return true;
        return variant.options[index] === want;
      })
    );
    const sellable = matches.filter((v) => v.available);
    return (sellable[0] ?? matches[0]) ?? null;
  }

  /**
   * For a mixed set the tiles can span colors but the line has one variant.
   * Charge the dearest color in the mix — the upcharge is for color work, so
   * billing the cheapest would undercharge every mixed set.
   */
  #chosenVariant() {
    const { mode, design, color } = this.#state;
    const designs = this.#optionValues(this.#designName());
    const colors = this.#optionValues(this.#colorName());

    if (mode === 'single') {
      // Multi-color artwork is two-color work, so it prices at the color tier
      // rather than whichever color happened to be selected.
      if (this.#isMultiColor() && colors.length > 1) {
        const priced = colors.map((c) => this.#variantFor(design, c)).filter(Boolean);
        if (priced.length) return priced.reduce((dear, v) => (v.price > dear.price ? v : dear));
      }
      return this.#variantFor(design ?? designs[0], color ?? colors[0]);
    }

    const used = mode === 'mixed'
      ? [...new Set(Object.entries(this.#state.counts).filter(([, n]) => n > 0).map(([key]) => key.split('||')[1]))]
      : colors;

    const candidates = (used.length ? used : colors)
      .map((c) => this.#variantFor(designs[0], c))
      .filter(Boolean);
    if (!candidates.length) return this.#variantFor(designs[0], colors[0]);
    return candidates.reduce((dearest, v) => (v.price > dearest.price ? v : dearest));
  }

  #caseVariant() {
    if (this.#state.package !== 'case') return null;
    const list = this.#conf()?.caseVariants ?? [];
    return list.find((v) => v.id === this.#state.caseVariantId) ?? list[0] ?? null;
  }

  #feeDollars() {
    if (this.#state.mode === 'mixed') return Math.max(0, Number(this.dataset.mixFee) || 0);
    if (this.#state.mode === 'random') return Math.max(0, Number(this.dataset.randomFee) || 0);
    return 0;
  }

  #totalCents() {
    const base = this.#chosenVariant()?.price ?? 0;
    const kase = this.#caseVariant()?.price ?? 0;
    return base + kase + this.#feeDollars() * 100;
  }

  #money(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  // ------------------------------------------------------------------ steps

  #onNext = () => {
    const problem = this.#validate(this.#steps[this.#stepIndex]);
    if (problem) return this.#showError(problem);
    this.#hideError();
    if (this.#stepIndex < this.#steps.length - 1) this.#stepIndex += 1;
    this.#render();
    this.#focusStep();
  };

  #onBack = () => {
    this.#hideError();
    if (this.#stepIndex > 0) this.#stepIndex -= 1;
    this.#render();
    this.#focusStep();
  };

  #focusStep() {
    // The scroll is ours to make, so focus must not also make one -- two
    // scrolls racing each other land somewhere neither of them meant.
    const title = this.querySelector(`[data-db-step="${this.#steps[this.#stepIndex]}"] .db__step-title`);
    if (title instanceof HTMLElement) {
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
    }
    this.#scrollToStep();
  }

  /** How much of the viewport the theme's sticky header is holding. */
  #headerOffset() {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    return Number.parseFloat(raw) || 60;
  }

  #smooth() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * A step change swaps the whole panel, so wherever the shopper was reading
   * is gone. Land them on the progress crumbs: it names the step they just
   * moved to and puts its first control right below the fold.
   */
  #scrollToStep() {
    const anchor = this.querySelector('[data-db-progress]') ?? this;
    const top = anchor.getBoundingClientRect().top + window.scrollY - this.#headerOffset() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: this.#smooth() });
  }

  /** @returns {string|null} a message naming exactly what is missing */
  #validate(step) {
    const size = this.#setSize();
    if (step === 'collection' && !this.#state.handle) return 'Choose a collection to continue.';
    if (step === 'collection' && !this.#product()) return 'Still loading that collection. Try again in a moment.';

    if (step === 'set' && this.#state.package === 'case' && !this.#caseVariant()) {
      return 'Choose a case color to continue.';
    }

    if (step === 'designs') {
      const hasDesigns = this.#optionValues(this.#designName()).length > 0;
      // Some products carry no Design option at all. Asking for one would trap
      // the shopper on a step with nothing to pick.
      if (hasDesigns && this.#state.mode === 'single' && !this.#state.design) {
        return 'Choose a design to continue.';
      }
      if (!hasDesigns && this.#state.mode !== 'single') this.#state.mode = 'single';
      if (hasDesigns && this.#state.mode === 'mixed') {
        const total = this.#assigned();
        if (total === 0) return `Assign your ${size} dominoes across the designs you want.`;
        if (total < size) {
          const left = size - total;
          return `You have assigned ${total} of ${size} dominoes. Assign ${left} more to continue.`;
        }
        if (total > size) {
          const over = total - size;
          return `You have assigned ${total} of ${size} dominoes. Remove ${over} to continue.`;
        }
      }
      const needsColor =
        !this.#isMultiColor() && this.#state.mode === 'single' && this.#optionValues(this.#colorName()).length;
      if (needsColor && !this.#state.color) return 'Choose a domino color to continue.';
    }
    return null;
  }

  #assigned() {
    return Object.values(this.#state.counts).reduce((sum, n) => sum + (Number(n) || 0), 0);
  }

  // ------------------------------------------------------------------ input

  #onChange = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-db-collection]')) {
      const handle = /** @type {HTMLInputElement} */ (target).value;
      this.#state.handle = handle;
      this.#state.design = null;
      this.#state.color = null;
      this.#state.counts = {};
      this.#state.caseVariantId = null;
      await this.#loadProduct(handle);
      this.#track('collection_selected', { handle });
      this.#hideError();
      this.#render();
      return;
    }

    if (target.matches('[data-db-package]')) {
      this.#state.package = /** @type {HTMLInputElement} */ (target).value;
      this.#render();
      return;
    }
    if (target.matches('[data-db-case-color]')) {
      this.#state.caseVariantId = Number(/** @type {HTMLInputElement} */ (target).value);
      this.#render();
      return;
    }
    if (target.matches('[data-db-mode]')) {
      this.#state.mode = /** @type {HTMLInputElement} */ (target).value;
      this.#hideError();
      this.#render();
      return;
    }
    if (target.matches('[data-db-design]')) {
      this.#state.design = /** @type {HTMLInputElement} */ (target).value;
      this.#classify(this.#state.design);
      this.#hideError();
      this.#render();
      return;
    }
    if (target.matches('[data-db-color]')) {
      this.#state.color = /** @type {HTMLInputElement} */ (target).value;
      this.#hideError();
      this.#render();
    }
  };

  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-db-count]')) return;
    const key = `${target.dataset.design}||${target.dataset.color ?? ''}`;
    const size = this.#setSize();

    let n = Math.floor(Number(target.value) || 0);
    if (n < 0) n = 0;

    // A set holds exactly as many tiles as it holds. Cap against what the rest
    // of the mix has already claimed rather than against the set size, or two
    // fields could each take 28 and only the review step would object.
    const claimed = this.#assigned() - (Number(this.#state.counts[key]) || 0);
    const room = Math.max(0, size - claimed);
    const asked = n;
    if (n > room) n = room;

    if (String(n) !== target.value) target.value = String(n);
    this.#state.counts[key] = n;
    target.closest('.db-mix')?.classList.toggle('db-mix--on', n > 0);
    this.#updateTally();
    this.#persist();

    // Say why the number changed under them; silently swallowing it reads as a
    // broken field. Any edit that lands cleanly clears whatever was showing --
    // the shopper has moved on from it.
    if (asked > room) {
      this.#showError(
        room === 0
          ? `All ${size} dominoes are assigned. Remove some to make room.`
          : `Only ${room} more ${room === 1 ? 'domino' : 'dominoes'} will fit in a set of ${size}.`
      );
    } else {
      this.#hideError();
    }
  };

  /**
   * The + and - buttons write through the field rather than to state directly,
   * so typing and tapping cannot disagree about what a cell holds.
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('[data-db-error-close]')) return this.#hideError();

    const toggle = target.closest('[data-db-tally-toggle]');
    if (toggle instanceof HTMLElement) {
      this.#tallyOpen = !this.#tallyOpen;
      return this.#updateTally();
    }

    // Dropping a pick writes through its own field, so the cell, the tally and
    // the total can never drift apart.
    const drop = target.closest('[data-db-drop]');
    if (drop instanceof HTMLElement) {
      const cell = this.#countInput(drop.dataset.design, drop.dataset.color);
      if (cell) {
        cell.value = '0';
        cell.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }

    const button = target.closest('[data-db-nudge]');
    if (!(button instanceof HTMLElement)) return;

    const input = button.parentElement?.querySelector('[data-db-count]');
    if (!(input instanceof HTMLInputElement)) return;

    const next = (Number(input.value) || 0) + Number(button.dataset.dbNudge ?? 0);
    input.value = String(Math.max(0, Math.min(this.#setSize(), next)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * @param {string|undefined} design
   * @param {string|undefined} color
   * @returns {HTMLInputElement|null}
   */
  #countInput(design, color) {
    for (const input of this.querySelectorAll('[data-db-count]')) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.design === design &&
        (input.dataset.color ?? '') === (color ?? '')
      ) {
        return input;
      }
    }
    return null;
  }

  /**
   * The running tally: how many dominoes are spoken for, and exactly which
   * designs they are. A collection can run to fifteen cells or more, so this
   * rides down the grid with the shopper instead of scrolling away.
   */
  #updateTally() {
    const size = this.#setSize();
    const total = this.#assigned();
    const picks = this.#picks();

    const counter = this.querySelector('[data-db-counter]');
    if (counter instanceof HTMLElement) {
      counter.textContent =
        total === size ? `${size} of ${size} — Complete` : `${total} of ${size} dominoes assigned`;
      counter.classList.toggle('db__counter--under', total < size);
      counter.classList.toggle('db__counter--over', total > size);
    }

    const tally = this.querySelector('[data-db-tally]');
    if (tally instanceof HTMLElement) tally.classList.toggle('db__tally--over', total > size);

    const fill = this.querySelector('[data-db-tally-fill]');
    if (fill instanceof HTMLElement) {
      fill.style.inlineSize = `${size ? Math.min(100, (total / size) * 100) : 0}%`;
    }

    const toggle = this.querySelector('[data-db-tally-toggle]');
    if (toggle instanceof HTMLElement) {
      toggle.hidden = picks.length === 0;
      // Nothing left to show closes the drawer, so it cannot reopen empty.
      if (!picks.length) this.#tallyOpen = false;
      toggle.textContent = this.#tallyOpen
        ? 'Hide selection'
        : `Show selection (${picks.length})`;
      toggle.setAttribute('aria-expanded', String(this.#tallyOpen));
    }

    const list = this.querySelector('[data-db-tally-list]');
    if (list instanceof HTMLElement) {
      list.hidden = !this.#tallyOpen || picks.length === 0;
      list.replaceChildren(...picks.map((pick) => this.#tallyChip(pick)));
    }

    this.#syncNudges(total, size);
  }

  /**
   * A full set greys out every +, so the limit is visible before it is hit
   * rather than only being explained after.
   * @param {number} total
   * @param {number} size
   */
  #syncNudges(total, size) {
    for (const button of this.querySelectorAll('[data-db-nudge]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const input = button.parentElement?.querySelector('[data-db-count]');
      const value = input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
      button.disabled = Number(button.dataset.dbNudge ?? 0) > 0 ? total >= size : value <= 0;
    }
  }

  /**
   * What is currently in the mix, in grid order rather than insertion order —
   * the list should read the same way the cells do.
   * @returns {{design: string, color: string, label: string, count: number}[]}
   */
  #picks() {
    const order = this.#optionValues(this.#designName());
    return Object.entries(this.#state.counts)
      .filter(([, n]) => Number(n) > 0)
      .map(([key, n]) => {
        const [design, color] = key.split('||');
        const multi = this.#artState.get(design) === 'single-file';
        return {
          design,
          color: color ?? '',
          label: color && !multi ? `${design} · ${color}` : design,
          count: Number(n),
        };
      })
      .sort((a, b) => order.indexOf(a.design) - order.indexOf(b.design));
  }

  /** @returns {HTMLLIElement} */
  #tallyChip({ design, color, label, count }) {
    const item = document.createElement('li');
    item.className = 'db__tally-chip';

    const text = document.createElement('span');
    // A badge rather than "x6": the multiplication sign sat next to the remove
    // button's own cross and the two read as the same thing.
    const strong = document.createElement('b');
    strong.className = 'db__tally-n';
    strong.textContent = String(count);
    text.append(label, strong);

    const drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'db__tally-drop';
    drop.dataset.dbDrop = '';
    drop.dataset.design = design;
    drop.dataset.color = color;
    drop.setAttribute('aria-label', `Remove ${label}`);
    drop.textContent = '\u00d7';

    item.append(text, drop);
    return item;
  }

  // ------------------------------------------------------------------ render

  #render() {
    const step = this.#steps[this.#stepIndex];

    for (const el of this.querySelectorAll('[data-db-step]')) {
      if (el instanceof HTMLElement) el.hidden = el.dataset.dbStep !== step;
    }
    for (const crumb of this.querySelectorAll('[data-db-crumb]')) {
      if (!(crumb instanceof HTMLElement)) continue;
      const index = this.#steps.indexOf(crumb.dataset.dbCrumb ?? '');
      crumb.classList.toggle('db__crumb--active', index === this.#stepIndex);
      crumb.classList.toggle('db__crumb--done', index < this.#stepIndex);
    }

    const back = this.querySelector('[data-db-back]');
    const next = this.querySelector('[data-db-next]');
    const add = this.querySelector('[data-db-add]');
    if (back instanceof HTMLElement) back.hidden = this.#stepIndex === 0;
    if (next instanceof HTMLElement) next.hidden = step === 'review';
    if (add instanceof HTMLElement) add.hidden = step !== 'review';

    if (step === 'set') this.#renderSet();
    if (step === 'designs') this.#renderDesigns();
    if (step === 'review') this.#renderReview();

    this.#persist();
  }

  #renderSet() {
    const conf = this.#conf();
    const cases = conf?.caseVariants ?? [];

    // The tile count belongs to the product, so the merchant writes {count}
    // and it is filled in here rather than typed into nine settings.
    const say = (selector, template) => {
      const node = this.querySelector(selector);
      if (node instanceof HTMLElement) {
        node.textContent = (template || '').replaceAll('{count}', String(this.#setSize()));
      }
    };
    say('[data-db-set-note]', this.dataset.setNote);
    say('[data-db-blurb-dominoes]', this.dataset.noteDominoes);
    say('[data-db-blurb-case]', this.dataset.noteCase);

    const option = this.querySelector('[data-db-case-option]');
    if (option instanceof HTMLElement) option.hidden = cases.length === 0;
    if (cases.length === 0 && this.#state.package === 'case') this.#state.package = 'dominoes';

    const dominoesPrice = this.querySelector('[data-db-price-dominoes]');
    if (dominoesPrice instanceof HTMLElement) {
      const v = this.#chosenVariant();
      dominoesPrice.textContent = v ? this.#money(v.price) : '';
    }
    const casePrice = this.querySelector('[data-db-price-case]');
    if (casePrice instanceof HTMLElement && cases[0]) {
      const v = this.#chosenVariant();
      casePrice.textContent = this.#money((v?.price ?? 0) + cases[0].price);
    }

    for (const input of this.querySelectorAll('[data-db-package]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.#state.package;
    }

    const wrap = this.querySelector('[data-db-case-colors]');
    const list = this.querySelector('[data-db-case-color-list]');
    // A single case color is not a choice — don't make anyone tap it.
    const needsChoice = this.#state.package === 'case' && cases.length > 1;
    if (wrap instanceof HTMLElement) wrap.hidden = !needsChoice;
    if (this.#state.package === 'case' && cases.length === 1) this.#state.caseVariantId = cases[0].id;

    if (list instanceof HTMLElement && needsChoice) {
      list.replaceChildren(
        ...cases.map((v) =>
          this.#card({
            name: 'db-case-color',
            value: String(v.id),
            attr: 'data-db-case-color',
            checked: this.#state.caseVariantId === v.id,
            label: v.title,
            meta: v.available ? '' : 'Unavailable',
            disabled: !v.available,
          })
        )
      );
    }
  }

  #renderDesigns() {
    const designs = this.#optionValues(this.#designName());
    const colors = this.#optionValues(this.#colorName());
    if (!designs.length) this.#state.mode = 'single';
    const mode = this.#state.mode;

    // Mixed and Random only mean something when there are designs to mix.
    const modes = this.querySelector('.db__modes');
    if (modes instanceof HTMLElement) modes.hidden = designs.length === 0;

    for (const input of this.querySelectorAll('[data-db-mode]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === mode;
    }
    const single = this.querySelector('[data-db-single]');
    const mixed = this.querySelector('[data-db-mixed]');
    const random = this.querySelector('[data-db-random]');
    if (single instanceof HTMLElement) single.hidden = mode !== 'single';
    if (mixed instanceof HTMLElement) mixed.hidden = mode !== 'mixed';
    if (random instanceof HTMLElement) random.hidden = mode !== 'random';

    // Land on the default before anything is drawn, so the cards draw checked
    // rather than flashing unselected -- and, more importantly, so the tiles
    // ask for the right artwork the first time. Products whose designs have
    // only per-color files have no plain file to fall back on, so a tile built
    // before the color was known showed nothing at all until a redraw.
    if (!this.#state.color) this.#state.color = this.#defaultColor(colors);

    if (mode === 'single') {
      const list = this.querySelector('[data-db-design-list]');
      if (list instanceof HTMLElement && !designs.length) list.replaceChildren();
      if (list instanceof HTMLElement && designs.length) {
        list.replaceChildren(
          ...designs.map((d) =>
            this.#card({
              name: 'db-design',
              value: d,
              attr: 'data-db-design',
              checked: this.#state.design === d,
              label: d,
              tile: true,
            })
          )
        );
      }
    }

    if (mode === 'mixed') {
      for (const d of designs) this.#classify(d);
      this.#renderMatrix(designs, colors);
    }

    // Random means the colors are ours to pick too, and a multi-color design
    // has nothing to choose — its artwork is fixed.
    const colorStep = this.querySelector('[data-db-color-step]');
    const showColor =
      colors.length > 0 && (mode === 'single' || !designs.length) && !this.#isMultiColor();
    if (colorStep instanceof HTMLElement) colorStep.hidden = !showColor;
    if (showColor) {
      const list = this.querySelector('[data-db-color-list]');
      if (list instanceof HTMLElement) {
        list.replaceChildren(
          ...colors.map((c) =>
            this.#card({
              name: 'db-color',
              value: c,
              attr: 'data-db-color',
              checked: this.#state.color === c,
              label: c,
              meta: this.#colorMeta(c),
            })
          )
        );
      }
    }
    if (mode === 'random' || mode === 'mixed') {
      this.#state.color = this.#state.color ?? this.#defaultColor(colors) ?? colors[0] ?? null;
    }
  }

  /** Shows the shopper why one color costs more than another. */
  #colorMeta(color) {
    const designs = this.#optionValues(this.#designName());
    const colors = this.#optionValues(this.#colorName());
    const here = this.#variantFor(this.#state.design ?? designs[0], color);
    const cheapest = colors
      .map((c) => this.#variantFor(this.#state.design ?? designs[0], c))
      .filter(Boolean)
      .reduce((min, v) => (v.price < min.price ? v : min), { price: Infinity });
    if (!here || !isFinite(cheapest.price) || here.price === cheapest.price) return '';
    return `+${this.#money(here.price - cheapest.price)}`;
  }

  /**
   * The mixed set as a grid of the things that can actually be ordered: one
   * cell per design per color it is printed in, each showing its own artwork.
   * A shopper choosing a mix is choosing pictures, so a table of names made
   * them guess; the cell is the picture, and the name is the caption.
   *
   * @param {string[]} designs
   * @param {string[]} colors
   */
  #renderMatrix(designs, colors) {
    const grid = this.querySelector('[data-db-matrix]');
    if (!(grid instanceof HTMLElement)) return;

    this.#consolidateCounts(designs, colors);

    const cells = [];
    for (const design of designs) {
      // Multi-color artwork looks the same whichever color is named, so it
      // gets one cell rather than an identical picture per color.
      const multi = this.#artState.get(design) === 'single-file';
      const shown = multi || !colors.length ? [null] : colors;
      for (const color of shown) cells.push(this.#mixCell(design, color, multi));
    }
    grid.replaceChildren(...cells);
    this.#updateTally();
  }

  /**
   * @param {string} design
   * @param {string|null} color - null for a multi-color design, or a product
   *   with no color option at all.
   * @param {boolean} multi
   * @returns {HTMLElement}
   */
  #mixCell(design, color, multi) {
    // A multi-color design still bills against a real variant, and the rule is
    // that it prices at the color tier -- so its count lives under the dearest
    // color, which is exactly what #chosenVariant then charges for the mix.
    const keyColor = multi ? this.#dearestColor(design) ?? '' : color ?? '';
    const value = Number(this.#state.counts[`${design}||${keyColor}`]) || 0;

    const cell = document.createElement('div');
    cell.className = 'db-mix';
    cell.classList.toggle('db-mix--on', value > 0);

    cell.append(this.#tileFace(design, multi ? null : color));

    const name = document.createElement('span');
    name.className = 'db-mix__name';
    name.textContent = design;
    cell.append(name);

    const caption = multi ? 'Multi-color' : color;
    if (caption) {
      const sub = document.createElement('span');
      sub.className = 'db-mix__color';
      sub.textContent = caption;
      cell.append(sub);
    }

    const label = caption ? `${design} — ${caption}` : design;
    const stepper = document.createElement('div');
    stepper.className = 'db-mix__stepper';

    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'db__count';
    input.min = '0';
    input.max = String(this.#setSize());
    input.step = '1';
    input.inputMode = 'numeric';
    input.value = String(value);
    input.setAttribute('aria-label', `${label} quantity`);
    input.dataset.dbCount = '';
    input.dataset.design = design;
    input.dataset.color = keyColor;

    stepper.append(
      this.#nudge(-1, `Remove one ${label}`),
      input,
      this.#nudge(1, `Add one ${label}`)
    );
    cell.append(stepper);
    return cell;
  }

  /**
   * @param {number} delta
   * @param {string} label
   * @returns {HTMLButtonElement}
   */
  #nudge(delta, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'db-mix__nudge';
    button.dataset.dbNudge = String(delta);
    button.setAttribute('aria-label', label);
    button.textContent = delta < 0 ? '\u2212' : '+';
    return button;
  }

  /** The color a design is dearest in — the tier a multi-color set bills at. */
  #dearestColor(design) {
    const colors = this.#optionValues(this.#colorName());
    let best = null;
    let price = -1;
    for (const color of colors) {
      const variant = this.#variantFor(design, color);
      if (variant && variant.price > price) {
        price = variant.price;
        best = color;
      }
    }
    return best ?? colors[0] ?? null;
  }

  /**
   * Folds a multi-color design's counts into the one cell it is drawn with.
   * Counts can land under another color from a session restored before the
   * artwork was classified, and an orphaned count still adds to the total —
   * so the tally would say 28 while the grid visibly showed fewer.
   *
   * @param {string[]} designs
   * @param {string[]} colors
   */
  #consolidateCounts(designs, colors) {
    if (!colors.length) return;
    for (const design of designs) {
      if (this.#artState.get(design) !== 'single-file') continue;
      const keep = `${design}||${this.#dearestColor(design) ?? ''}`;
      let total = 0;
      for (const color of colors) {
        const key = `${design}||${color}`;
        total += Number(this.#state.counts[key]) || 0;
        if (key !== keep) delete this.#state.counts[key];
      }
      if (total > 0) this.#state.counts[keep] = total;
      else delete this.#state.counts[keep];
    }
  }

  /**
   * A domino face for one design, in one color where the design is printed per
   * color. The artwork and the name both go in and whichever is real wins:
   * Liquid cannot ask whether an asset exists, so the image proves itself by
   * loading and the name stands in until it does.
   *
   * @param {string} design
   * @param {string|null} [color]
   * @returns {HTMLElement}
   */
  #tileFace(design, color) {
    const face = document.createElement('span');
    face.className = 'db-tile';

    const name = document.createElement('span');
    name.className = 'db-tile__name';
    name.textContent = design;

    const art = this.#conf()?.art?.[design];
    if (art?.['']) {
      // A single-color design gets one file per color; a multi-color design has
      // just the one. Try the color-specific name first and fall back, so both
      // kinds work from the filenames alone with nothing to configure.
      //
      // With no color yet — a product whose colors do not include the default —
      // preview the first one rather than asking for a plain file that a
      // per-color design does not have. And once a design is known to have just
      // the one file, stop asking for a per-color name that will only 404 again
      // on every redraw.
      const known = this.#artState.get(design);
      const fallback = known === 'single-file' ? null : this.#optionValues(this.#colorName())[0];
      const wanted = known === 'single-file' ? null : color ?? fallback;
      const colorUrl = wanted ? art[wanted] : null;
      const candidates = colorUrl ? [colorUrl, art['']] : [art['']];

      // Probe with a detached Image rather than a hidden <img>: a hidden element
      // with loading="lazy" is never fetched, so its load event never fires and
      // it can never reveal itself. Insert only once it decodes, which also
      // means no broken-image flash when there is no artwork.
      const tryNext = (index) => {
        const url = candidates[index];
        if (!url) return;
        const probe = new Image();
        probe.addEventListener('load', () => {
          const art = document.createElement('img');
          art.className = 'db-tile__art';
          art.alt = '';
          art.src = url;
          face.prepend(art);
          name.hidden = true;
        });
        probe.addEventListener('error', () => tryNext(index + 1));
        probe.src = url;
      };
      tryNext(0);
    }

    face.append(name);
    return face;
  }

  /** @returns {HTMLLabelElement} */
  #card({ name, value, attr, checked, label, meta = '', tile = false, disabled = false }) {
    const wrap = document.createElement('label');
    wrap.className = 'db-card';

    const input = document.createElement('input');
    input.className = 'db-card__input';
    input.type = 'radio';
    input.name = name;
    input.value = value;
    input.checked = Boolean(checked);
    input.disabled = disabled;
    input.setAttribute(attr, '');

    const body = document.createElement('span');
    body.className = 'db-card__body';

    if (tile) body.append(this.#tileFace(label, this.#state.color));

    const name_ = document.createElement('span');
    name_.className = 'db-card__name';
    name_.textContent = label;
    body.append(name_);

    if (meta) {
      const m = document.createElement('span');
      m.className = 'db-card__meta';
      m.textContent = meta;
      body.append(m);
    }

    wrap.append(input, body);
    return wrap;
  }

  #renderReview() {
    const conf = this.#conf();
    const variant = this.#chosenVariant();
    const kase = this.#caseVariant();
    const rows = [];

    rows.push(['Collection', conf?.title ?? '—']);
    rows.push(['Set', kase ? `Dominoes + Display Case (${kase.title})` : 'Dominoes only']);
    rows.push(['Design mode', { single: 'One design', mixed: 'Mixed designs', random: 'Let us choose' }[this.#state.mode]]);

    if (this.#state.mode === 'single') rows.push(['Design', this.#state.design ?? '—']);
    if (this.#state.mode === 'mixed') rows.push(['Designs', this.#describeCounts() || '—']);
    // Only a single-design set has one color. In a mix the colors are in the
    // Designs line above, and under Random they are ours to pick.
    if (this.#state.mode === 'single') {
      rows.push(['Color', this.#isMultiColor() ? 'Multi-color' : this.#state.color ?? '—']);
    }
    rows.push(['Production', conf?.production ?? 'Made to Order']);

    const summary = this.querySelector('[data-db-summary]');
    if (summary instanceof HTMLElement) {
      summary.replaceChildren(
        ...rows.flatMap(([label, value]) => {
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = String(value);
          return [dt, dd];
        })
      );
    }

    const total = this.querySelector('[data-db-total]');
    if (total instanceof HTMLElement) total.textContent = this.#money(this.#totalCents());

    const label = this.querySelector('[data-db-add-label]');
    if (label instanceof HTMLElement && variant) {
      label.textContent = `Add to cart — ${this.#money(this.#totalCents())}`;
    }
    this.#track('configuration_completed', { handle: this.#state.handle, mode: this.#state.mode });
  }

  #describeCounts() {
    return Object.entries(this.#state.counts)
      .filter(([, n]) => n > 0)
      .map(([key, n]) => {
        const [design, color] = key.split('||');
        // A multi-color design is billed against a color variant but is not
        // printed in one, so naming that color on the order would mislead.
        const multi = this.#artState.get(design) === 'single-file';
        return color && !multi ? `${design} (${color}) × ${n}` : `${design} × ${n}`;
      })
      .join(', ');
  }

  // ------------------------------------------------------------------ cart

  #onAdd = async () => {
    for (const step of this.#steps.slice(0, 3)) {
      const problem = this.#validate(step);
      if (problem) {
        this.#stepIndex = this.#steps.indexOf(step);
        this.#render();
        return this.#showError(problem);
      }
    }

    const variant = this.#chosenVariant();
    if (!variant) return this.#showError('That combination is not available. Try another design or color.');

    const button = this.querySelector('[data-db-add]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    this.#hideError();

    const conf = this.#conf();
    const setId = `sg-${Math.abs(Date.now() % 0xffffff).toString(16)}`;
    const properties = {
      Collection: conf?.title ?? '',
      'Set Type': this.#caseVariant() ? 'Dominoes + Display Case' : 'Dominoes only',
      'Design Mode': { single: 'Single', mixed: 'Mixed', random: 'Random' }[this.#state.mode],
    };

    if (this.#state.mode === 'single') properties.Design = this.#state.design;
    if (this.#state.mode === 'mixed') properties.Designs = this.#describeCounts();
    if (this.#state.mode === 'random') properties.Designs = 'Random mix — designs and colors chosen by So Genius IO';
    if (this.#state.mode === 'single') {
      properties['Domino Color'] = this.#isMultiColor() ? 'Multi-color' : this.#state.color;
    }
    properties['_Set ID'] = setId;
    properties['_Set Size'] = String(this.#setSize());
    properties['_Production'] = conf?.production ?? 'Made to Order';

    const items = [{ id: variant.id, quantity: 1, properties }];

    const kase = this.#caseVariant();
    if (kase) {
      items.push({
        id: kase.id,
        quantity: 1,
        properties: { For: conf?.title ?? 'Domino set', '_Set ID': setId },
      });
    }

    const fee = this.#feeDollars();
    const feeHandle = this.dataset.feeHandle;
    if (fee > 0 && feeHandle) {
      const feeProduct = await fetch(`/products/${feeHandle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      const feeVariant = feeProduct?.variants?.[0]?.id;
      if (!feeVariant) {
        this.#restoreButton(button);
        return this.#showError('We could not add the upgrade charge, so nothing was added. Please try again.');
      }
      items.push({
        id: feeVariant,
        quantity: fee,
        properties: { Covers: this.#state.mode === 'mixed' ? 'Mixed designs' : 'Random mix', '_Set ID': setId },
      });
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(`cart/add returned ${response.status}`);

      this.#track('custom_order_added_to_cart', { handle: this.#state.handle, mode: this.#state.mode });
      sessionStorage.removeItem(this.#storageKey);
      location.href = '/cart';
    } catch (error) {
      console.error('[domino-builder] add to cart failed', error);
      this.#restoreButton(button);
      this.#showError('We could not add your set to the cart. Please try again.');
    }
  };

  #restoreButton(button) {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  // ------------------------------------------------------------------ misc

  /**
   * Errors arrive as a toast pinned to the viewport rather than a line in the
   * page, which on a long grid could be a screen away from whatever the
   * shopper just pressed.
   */
  #showError(message) {
    const error = this.querySelector('[data-db-error]');
    if (!(error instanceof HTMLElement)) return;

    const text = error.querySelector('[data-db-error-text]') ?? error;
    text.textContent = message;

    // Sit clear of the action bar, which is only sticky on small screens --
    // measured rather than guessed, so it tracks the breakpoint by itself.
    const actions = this.querySelector('.db__actions');
    const lift =
      actions instanceof HTMLElement && getComputedStyle(actions).position === 'sticky'
        ? actions.offsetHeight
        : 0;
    error.style.setProperty('--db-toast-lift', `${lift}px`);

    error.hidden = false;
    // Restart the entrance whenever a fresh problem lands on a visible toast.
    error.classList.remove('db__error--in');
    void error.offsetWidth;
    error.classList.add('db__error--in');

    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => this.#hideError(), 6000);
  }

  #hideError() {
    clearTimeout(this.#errorTimer);
    const error = this.querySelector('[data-db-error]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.classList.remove('db__error--in');
    }
  }

  /** Custom events alongside Shopify's own, never instead of them. */
  #track(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  #persist() {
    try {
      sessionStorage.setItem(
        this.#storageKey,
        JSON.stringify({ step: this.#stepIndex, state: this.#state })
      );
    } catch {
      // Private browsing, or storage disabled. The builder still works.
    }
  }

  async #restore() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.#storageKey) ?? 'null');
    } catch {
      return;
    }
    if (!saved?.state?.handle || !this.#config.has(saved.state.handle)) return;

    this.#state = { ...this.#state, ...saved.state };
    this.#stepIndex = Math.min(Number(saved.step) || 0, this.#steps.length - 1);
    await this.#loadProduct(this.#state.handle);

    const input = this.querySelector(`[data-db-collection][value="${CSS.escape(this.#state.handle)}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
    this.#render();
  }
}

if (!customElements.get('domino-builder-component')) {
  customElements.define('domino-builder-component', DominoBuilder);
}
